import { useEffect, useRef, useState } from "react";
import type { MapRef } from "react-map-gl/maplibre";

export interface FeaturePropertyInfo {
  names: string[];
  values: Record<string, string[]>;
}

const EMPTY: FeaturePropertyInfo = { names: [], values: {} };

// Global map ref — set by MapPreview
let globalMapRef: React.RefObject<MapRef | null> | null = null;

export function setGlobalMapRef(ref: React.RefObject<MapRef | null>) {
  globalMapRef = ref;
}

/**
 * Queries currently loaded features from a source and extracts
 * available property names and their unique values.
 */
export function useFeatureProperties(
  sourceId: string | undefined,
  sourceLayer: string | undefined,
): FeaturePropertyInfo {
  const [info, setInfo] = useState<FeaturePropertyInfo>(EMPTY);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!sourceId || !globalMapRef) {
      setInfo(EMPTY);
      return;
    }

    // Debounce — re-query when source/layer changes or map moves
    const query = () => {
      const map = globalMapRef?.current?.getMap();
      if (!map) return;

      try {
        if (!map.isStyleLoaded()) return;
        if (!map.getSource(sourceId!)) return;
        const features = map.querySourceFeatures(sourceId!, {
          sourceLayer: sourceLayer || undefined,
        });

        const names = new Set<string>();
        const values: Record<string, Set<string>> = {};
        const maxFeatures = 500; // Sample limit
        const maxValuesPerProp = 50;

        for (let i = 0; i < Math.min(features.length, maxFeatures); i++) {
          const props = features[i]?.properties;
          if (!props) continue;

          for (const [key, val] of Object.entries(props)) {
            names.add(key);
            if (!values[key]) values[key] = new Set();
            if ((values[key]?.size ?? 0) < maxValuesPerProp && val != null) {
              values[key]?.add(String(val));
            }
          }
        }

        setInfo({
          names: [...names].sort(),
          values: Object.fromEntries(
            Object.entries(values).map(([k, v]) => [k, [...v].sort()]),
          ),
        });
      } catch {
        setInfo(EMPTY);
      }
    };

    // Query immediately and on a short delay (tiles may still be loading)
    query();
    timer.current = setTimeout(query, 1500);

    return () => clearTimeout(timer.current);
  }, [sourceId, sourceLayer]);

  return info;
}
